import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFeather, FiZap, FiShield, FiCheckCircle, FiRefreshCw } = FiIcons;

// Canned continuations per voice — typed out word-by-word to feel like the app writing with you.
const VOICES = {
  literary: {
    label: 'Literary',
    accent: 'from-primary-500 to-primary-600',
    lines: [
      ' The light came in low across the floorboards, the way it always did in the hour before everyone woke, and for a moment the house held its breath with me.',
      ' I had learned, by then, that some doors are only quiet because of what waits on the other side of them.',
      ' Outside, the first train went by like a long exhale, and I let myself believe — just for the length of it — that leaving could feel like arriving.',
    ],
  },
  warm: {
    label: 'Warm',
    accent: 'from-secondary-500 to-amber-600',
    lines: [
      ' She set two cups down without asking, the way she always did, and somehow that small certainty was the kindest thing anyone had said to me all year.',
      ' We laughed at nothing, the good kind of nothing, and the kitchen filled up with it until the cold outside stopped mattering.',
      ' "Stay," he said, not like a question, and I realized I had been waiting my whole life for someone to make it that simple.',
    ],
  },
  punchy: {
    label: 'Punchy',
    accent: 'from-slate-600 to-slate-800',
    lines: [
      ' I checked the time. Forty seconds. Long enough to be wrong, not long enough to fix it.',
      ' The door clicked. I did not move. Moving was how they found you.',
      ' One rule had kept me alive this far: never trust a hallway that quiet.',
    ],
  },
  alice: {
    label: 'Alice in Wonderland',
    accent: 'from-fuchsia-500 to-amber-500',
    starter: 'Alice was beginning to get very tired of sitting by her sister on the bank, and of having nothing to do.',
    lines: [
      ' So she was considering, in her own mind, whether the pleasure of making a daisy-chain would be worth the trouble of getting up and picking the daisies, when suddenly a White Rabbit with pink eyes ran close by her.',
      ' "Curiouser and curiouser!" cried Alice, quite forgetting, in her surprise, how to speak good English at all.',
      ' The Rabbit took a watch out of its waistcoat-pocket, looked at it, and hurried on — for it would never do to be late, though late for what, nobody could rightly say.',
      ' "If everybody minded their own business," the Duchess growled, "the world would go round a great deal faster than it does."',
    ],
  },
};

const countWords = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);
const countSentences = (t) => (t.match(/[.!?]+/g) || []).length;

const Stat = ({ label, value }) => (
  <div className="bg-white/70 rounded-lg px-3 py-2 text-center border border-parchment-200">
    <div className="text-lg font-bold text-gray-900 tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
  </div>
);

const STARTER = 'I never learned my mother’s face, only the shape of the door she left through.';

const LiveWriteDemo = () => {
  const [text, setText] = useState(STARTER);
  const [voice, setVoice] = useState('literary');
  const [streaming, setStreaming] = useState(false);
  const taRef = useRef(null);
  const streamRef = useRef(null);

  const words = countWords(text);
  const sentences = countSentences(text);
  const avgLen = sentences ? Math.round(words / sentences) : words;
  // Authenticity climbs as the writer adds their own words (purely client-side illustration).
  const voiceMatch = Math.min(97, 84 + Math.min(13, Math.floor(words / 6)));
  const human = Math.min(98, voiceMatch + 1);

  useEffect(() => () => clearInterval(streamRef.current), []);

  const continueInVoice = () => {
    if (streaming) return;
    const bank = VOICES[voice].lines;
    const line = bank[Math.floor((words + sentences) % bank.length)];
    const tokens = line.match(/\s*\S+/g) || [];
    let i = 0;
    setStreaming(true);
    streamRef.current = setInterval(() => {
      setText((prev) => prev + tokens[i]);
      i += 1;
      if (taRef.current) taRef.current.scrollTop = taRef.current.scrollHeight;
      if (i >= tokens.length) {
        clearInterval(streamRef.current);
        setStreaming(false);
      }
    }, 55);
  };

  const reset = () => {
    clearInterval(streamRef.current);
    setStreaming(false);
    setText(STARTER);
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-3">
          <SafeIcon icon={FiFeather} className="w-4 h-4" /> Try it now — no signup
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Don&apos;t just read about it. <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Write a line.</span></h2>
        <p className="text-gray-600 mt-2 max-w-2xl mx-auto">Type anything below, then let the studio carry it on in your voice. Watch it stay human as you go.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Editor */}
        <div className="lg:col-span-2 bg-parchment-50 rounded-2xl border border-parchment-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-parchment-200 bg-white/60">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className="w-2.5 h-2.5 rounded-full bg-red-300" /><span className="w-2.5 h-2.5 rounded-full bg-amber-300" /><span className="w-2.5 h-2.5 rounded-full bg-green-300" />
              <span className="ml-2 font-medium text-gray-500">untitled-chapter.md</span>
            </div>
            <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700">
              <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> reset
            </button>
          </div>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="w-full h-64 resize-none bg-transparent px-6 py-5 text-gray-800 leading-relaxed focus:outline-none"
            style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.15rem' }}
            placeholder="Start your story here…"
          />
          <div className="px-4 py-3 border-t border-parchment-200 bg-white/60 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-1">Voice</span>
              <div className="flex flex-wrap items-center gap-1.5 bg-parchment-100 rounded-lg p-1">
                {Object.entries(VOICES).map(([k, v]) => (
                  <button key={k} onClick={() => setVoice(k)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${voice === k ? `text-white bg-gradient-to-r ${v.accent} shadow` : 'text-gray-500 hover:text-gray-800'}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {VOICES[voice].starter && (
                <button onClick={() => { setText(VOICES[voice].starter); }}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800">
                  <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Load the famous opening, then continue it
                </button>
              )}
              <button onClick={continueInVoice} disabled={streaming}
                className={`sm:ml-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white transition-all ${streaming ? 'bg-gray-300 cursor-wait' : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 hover:scale-[1.02]'}`}>
                <SafeIcon icon={FiZap} className={`w-4 h-4 ${streaming ? 'animate-pulse' : ''}`} />
                {streaming ? `Writing in ${VOICES[voice].label}…` : 'Continue in your voice'}
              </button>
            </div>
          </div>
        </div>

        {/* Live panel */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Live, as you type</div>
            <div className="grid grid-cols-3 gap-2 mb-5">
              <Stat label="Words" value={words} />
              <Stat label="Sentences" value={sentences} />
              <Stat label="Avg / sent." value={avgLen} />
            </div>
            {/* Authenticity gauge */}
            <div className="relative flex flex-col items-center justify-center py-2">
              <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#e7e5e4" strokeWidth="10" />
                <motion.circle cx="60" cy="60" r="52" fill="none" stroke="url(#grad)" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 52}
                  animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - voiceMatch / 100) }}
                  transition={{ type: 'spring', stiffness: 60, damping: 14 }} />
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#059669" /><stop offset="100%" stopColor="#d97706" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute top-1/2 -translate-y-1/2 text-center">
                <div className="text-3xl font-bold text-gray-900 tabular-nums">{voiceMatch}%</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">in your voice</div>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-gray-600"><SafeIcon icon={FiShield} className="w-4 h-4 text-green-500" /> Reads as human</span>
                <span className="font-bold text-green-600 tabular-nums">{human}%</span>
              </div>
              <AnimatePresence>
                {words > 12 && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Passes AI detection — sounds like a person wrote it.
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 text-center leading-relaxed">A live preview of the writing experience. In the studio, your voice model is trained on your own pages.</p>
        </div>
      </div>
    </section>
  );
};

export default LiveWriteDemo;
